import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Globe } from "lucide-react";
import { LOGO_URL, PHONE, PHONE_TEL } from "../lib/assets";
import { useLang } from "../context/LangContext";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // Scroll listener attached once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "pricing", label: t.nav.pricing },
    { id: "docs", label: t.nav.docs },
    { id: "gallery", label: t.nav.gallery },
    { id: "faq", label: t.nav.faq },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar-container"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-brand-cream/90 backdrop-blur-md border-b border-brand-border-soft shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <button
          onClick={() => handleNav("hero")}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img src={LOGO_URL} alt="BIU BIU DRIVING" className="h-12 w-12 object-contain" />
          <div className="hidden sm:flex flex-col items-start leading-tight">
            <span className="font-display font-extrabold text-brand-navy text-lg tracking-tight">BIU BIU DRIVING</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand-orange font-bold">Școală de șoferi</span>
            <span className="text-[10px] tracking-wider text-brand-muted font-medium italic mt-0.5">Building Better Drivers</span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => handleNav(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="nav-link text-brand-navy font-medium text-sm hover:text-brand-orange transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setLang(lang === "ro" ? "en" : "ro")}
            data-testid="nav-lang-toggle"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-brand-border-soft text-brand-navy text-xs font-bold uppercase hover:bg-brand-navy hover:text-white transition-all"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "ro" ? "EN" : "RO"}
          </button>

          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="nav-phone-cta"
            className="hidden md:inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md"
          >
            <Phone className="w-4 h-4" />
            <span className="text-sm">{PHONE}</span>
          </a>

          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-full border border-brand-border-soft text-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-border-soft" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => handleNav(l.id)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="text-left px-4 py-3 rounded-xl text-brand-navy font-medium hover:bg-brand-cream"
              >
                {l.id === "faq" ? t.nav.faqFull : l.label}
              </button>
            ))}
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="mobile-phone-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-orange text-white font-semibold px-5 py-3 rounded-full"
            >
              <Phone className="w-4 h-4" /> {PHONE}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
